/* eslint-disable react-refresh/only-export-components */
/**
 * DebugPavilion —— 画布渲染层抽象（SVG+WASM 混合渲染）。
 *
 * CanvasRenderer 接口统一 render/layout/hitTest 三种能力：
 *  - DomRenderer：包装现有 DOM 渲染行为（dnd-kit 拖放 + RuntimeRenderer），零回归；
 *  - SvgRenderer：SVG 渲染模式——组件树 → SVG 元素（rect/text/image 基本映射），
 *    布局与命中测试走 wasm-geometry（WASM 或纯 TS 兜底），拖拽时用 snapToGuides
 *    做边缘/中心对齐吸附并绘制参考线，松手按插入位调用 moveChild 重排。
 *
 * 几何契约见 src/lib/builder/wasm-geometry.ts。
 */

import { useDroppable } from "@dnd-kit/core";
import { useSortable, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import type { ComponentNode } from "@shared/types";

import { useBuilderStore } from "@/lib/builder/builder-store";
import { getMaterial } from "@/lib/builder/materials";
import { cn } from "@/lib/utils";
import {
  computeLayout,
  DEFAULT_SNAP_THRESHOLD,
  hitTest as geometryHitTest,
  snapToGuides,
  type LayoutRect,
  type SnapGuide,
} from "@/lib/builder/wasm-geometry";

import { RuntimeRenderer } from "./runtime-renderer";

export const CANVAS_DROP_ZONE_ID = "canvas-drop-zone";

export type CanvasRenderMode = "dom" | "svg";

export interface CanvasRendererContext {
  /** 当前页布局根节点。 */
  node: ComponentNode;
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onMoveChild: (parentId: string, fromIndex: number, toIndex: number) => void;
}

/** 渲染层抽象：render 产出视图，layout/hitTest 提供同一份几何能力。 */
export interface CanvasRenderer {
  readonly mode: CanvasRenderMode;
  render(ctx: CanvasRendererContext): React.ReactNode;
  /** 组件树 -> 布局矩形（pre-order，绝对坐标）。 */
  layout(ctx: CanvasRendererContext): LayoutRect[];
  /** 命中测试：返回最上层命中的节点 id（未命中 null）。 */
  hitTest(ctx: CanvasRendererContext, x: number, y: number): string | null;
}

// ===========================================================================
// DomRenderer —— 现有 DOM 渲染行为（自 builder-canvas.tsx 原样迁入）
// ===========================================================================

/** 支持原地编辑文本的组件类型 → 编辑写回的 props 键。 */
const TEXT_EDITABLE_PROPS: Record<string, string> = {
  text: "content",
  button: "label",
};

function EditableTextOverlay({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (text: string) => void;
  onCancel: () => void;
}) {
  const [element, setElement] = React.useState<HTMLDivElement | null>(null);

  const setRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      setElement(el);
      if (el && el.innerText !== initial) {
        el.innerText = initial;
      }
    },
    [initial],
  );

  React.useEffect(() => {
    if (!element) return;
    element.focus();
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [element]);

  const commit = () => {
    onCommit(element?.innerText ?? "");
  };

  return (
    <div
      ref={setRef}
      contentEditable
      suppressContentEditableWarning
      data-testid="inline-text-editor"
      className="min-h-[24px] cursor-text whitespace-pre-wrap rounded border border-primary/50 bg-background p-1 text-sm outline-none ring-2 ring-primary/30"
      onBlur={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          (e.currentTarget as HTMLElement).blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
      onDoubleClick={(e) => e.stopPropagation()}
    />
  );
}

function CanvasNode({ node, depth = 0 }: { node: ComponentNode; depth?: number }) {
  const { selectedNodeId, selectNode, deleteNode, updateNodeProps } = useBuilderStore();
  const [editing, setEditing] = React.useState(false);
  const material = getMaterial(node.type);
  const isSelected = selectedNodeId === node.id;
  const isContainer = material?.allowChildren ?? false;
  const editableProp = isContainer ? null : TEXT_EDITABLE_PROPS[node.type] ?? null;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    data: { node },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const children = node.children || [];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative rounded-md border transition-colors",
        isSelected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-transparent hover:border-dashed hover:border-muted-foreground/30",
        depth > 0 && "mt-2",
      )}
      onClick={(e) => {
        e.stopPropagation();
        selectNode(node.id);
      }}
      onDoubleClick={(e) => {
        // 双击文字类组件（Text/Button 等）进入原地编辑态
        if (editableProp && !editing) {
          e.stopPropagation();
          setEditing(true);
        }
      }}
    >
      <div className={cn("flex items-center gap-1 px-2 py-1", isSelected && "bg-primary/5")}>
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab text-muted-foreground hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="text-xs font-medium text-muted-foreground">{material?.label || node.type}</span>
        {isSelected && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              deleteNode(node.id);
            }}
            className="ml-auto text-destructive hover:text-destructive/80"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
        {editableProp && (
          <span className="ml-1 rounded bg-muted px-1 text-[10px] text-muted-foreground" title="双击文本内容可原地编辑">
            双击编辑
          </span>
        )}
      </div>

      {isContainer ? (
        <div className="min-h-[48px] p-2">
          <SortableContext items={children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {children.map((child) => (
              <CanvasNode key={child.id} node={child} depth={depth + 1} />
            ))}
            {children.length === 0 && (
              <div className="rounded border border-dashed border-muted-foreground/30 p-3 text-center text-xs text-muted-foreground">
                拖拽组件到此处
              </div>
            )}
          </SortableContext>
        </div>
      ) : editing && editableProp ? (
        <div className="p-2">
          <EditableTextOverlay
            initial={String(node.props[editableProp] ?? "")}
            onCommit={(text) => {
              updateNodeProps(node.id, { [editableProp]: text });
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : (
        <div className="pointer-events-none p-2 opacity-80">
          <RuntimeRenderer node={node} />
        </div>
      )}
    </div>
  );
}

function DropZone() {
  const { isOver, setNodeRef } = useDroppable({ id: CANVAS_DROP_ZONE_ID });
  return (
    <div
      ref={setNodeRef}
      data-testid={CANVAS_DROP_ZONE_ID}
      className={cn(
        "mt-4 flex items-center justify-center gap-2 rounded-lg border border-dashed p-4 text-sm transition-colors",
        isOver
          ? "border-primary bg-primary/10 text-primary"
          : "border-muted-foreground/30 text-muted-foreground hover:border-primary/50",
      )}
    >
      <Plus className="h-4 w-4" />
      <span>拖放组件到此处</span>
    </div>
  );
}

function DomCanvas({ node }: { node: ComponentNode }) {
  return (
    <>
      <CanvasNode node={node} />
      <DropZone />
    </>
  );
}

export class DomRenderer implements CanvasRenderer {
  readonly mode = "dom" as const;

  render(ctx: CanvasRendererContext): React.ReactNode {
    return <DomCanvas node={ctx.node} />;
  }

  layout(ctx: CanvasRendererContext): LayoutRect[] {
    return computeLayout(ctx.node);
  }

  hitTest(ctx: CanvasRendererContext, x: number, y: number): string | null {
    return geometryHitTest(x, y, this.layout(ctx));
  }
}

// ===========================================================================
// SvgRenderer —— SVG 渲染模式（布局/命中测试走 wasm-geometry，拖拽吸附 snapToGuides）
// ===========================================================================

/** 节点显示文案：Text/Button 显示内容，其余显示物料标签。 */
function nodeLabel(node: ComponentNode): string {
  const props = node.props ?? {};
  if (node.type === "Text") return String(props.content ?? "");
  if (node.type === "Button") return String(props.label ?? "");
  if (node.type === "Input" || node.type === "Textarea") return String(props.placeholder ?? "");
  return getMaterial(node.type)?.label ?? node.type;
}

function truncate(text: string, maxChars: number): string {
  return text.length > maxChars ? `${text.slice(0, Math.max(1, maxChars - 1))}…` : text;
}

function collectNodes(node: ComponentNode, out: Map<string, ComponentNode> = new Map()): Map<string, ComponentNode> {
  out.set(node.id, node);
  for (const child of node.children ?? []) collectNodes(child, out);
  return out;
}

/** 父容器主轴方向（用于拖拽重排的插入位判定）。 */
function parentAxis(node: ComponentNode | undefined): "row" | "column" {
  if (!node) return "column";
  const direction = String(node.props?.direction ?? "");
  if (node.type === "Flex") return direction === "row" ? "row" : "column";
  if (node.type === "Container" || node.type === "Stack") return direction === "row" ? "row" : "column";
  return "column";
}

interface SvgCanvasProps {
  node: ComponentNode;
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onMoveChild: (parentId: string, fromIndex: number, toIndex: number) => void;
}

interface DragState {
  id: string;
  parentId: string;
  fromIndex: number;
  startX: number;
  startY: number;
  rect: LayoutRect;
}

export function SvgCanvas({
  node,
  selectedNodeId,
  hoveredNodeId,
  onSelect,
  onHover,
  onMoveChild,
}: SvgCanvasProps) {
  const rects = React.useMemo(() => computeLayout(node), [node]);
  const nodeMap = React.useMemo(() => collectNodes(node), [node]);
  const rootRect = rects.find((r) => r.depth === 0);
  const svgRef = React.useRef<SVGSVGElement | null>(null);

  const [drag, setDrag] = React.useState<DragState | null>(null);
  const [pointer, setPointer] = React.useState<{ x: number; y: number } | null>(null);

  // SVG 模式下也承接材料拖放（dnd-kit 需要 DOM droppable 节点）
  const { setNodeRef: setDroppableRef } = useDroppable({ id: CANVAS_DROP_ZONE_ID });

  const canvasWidth = rootRect?.width ?? 0;
  const canvasHeight = rootRect?.height ?? 0;

  /** 拖拽预览：原始矩形随指针平移，再对兄弟矩形做边缘/中心吸附。 */
  const preview = React.useMemo(() => {
    if (!drag || !pointer) return null;
    const proposed = {
      x: drag.rect.x + (pointer.x - drag.startX),
      y: drag.rect.y + (pointer.y - drag.startY),
      width: drag.rect.width,
      height: drag.rect.height,
    };
    const others = rects.filter((r) => r.parentId === drag.parentId && r.id !== drag.id);
    const snap = snapToGuides(proposed, others, DEFAULT_SNAP_THRESHOLD);
    return {
      rect: { ...proposed, x: proposed.x + snap.dx, y: proposed.y + snap.dy },
      guides: snap.guides,
    };
  }, [drag, pointer, rects]);

  /** 插入位：按预览中心与兄弟中点比较（row 用 X，其余用 Y）。 */
  const insertionIndex = React.useMemo(() => {
    if (!drag) return 0;
    if (!preview) return drag.fromIndex;
    const siblings = rects.filter((r) => r.parentId === drag.parentId && r.id !== drag.id);
    const axis = parentAxis(nodeMap.get(drag.parentId));
    const center = axis === "row" ? preview.rect.x + preview.rect.width / 2 : preview.rect.y + preview.rect.height / 2;
    return siblings.filter((s) => {
      const sc = axis === "row" ? s.x + s.width / 2 : s.y + s.height / 2;
      return sc < center;
    }).length;
  }, [drag, preview, rects, nodeMap]);

  const toSvgPoint = (e: { clientX: number; clientY: number }): { x: number; y: number } => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const b = svg.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top };
  };

  const startDrag = (r: LayoutRect, e: React.PointerEvent) => {
    if (r.depth === 0 || r.parentId === "") return;
    e.stopPropagation();
    const siblings = rects
      .filter((s) => s.parentId === r.parentId)
      .sort((a, b) => a.y - b.y || a.x - b.x);
    const fromIndex = siblings.findIndex((s) => s.id === r.id);
    setDrag({ id: r.id, parentId: r.parentId, fromIndex, startX: toSvgPoint(e).x, startY: toSvgPoint(e).y, rect: r });
    setPointer(toSvgPoint(e));
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    setPointer(toSvgPoint(e));
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!drag) return;
    (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
    if (preview && insertionIndex !== drag.fromIndex) {
      onMoveChild(drag.parentId, drag.fromIndex, insertionIndex);
    }
    setDrag(null);
    setPointer(null);
  };

  const guides: SnapGuide[] = preview?.guides ?? [];
  const isRootSelected = selectedNodeId === node.id;

  return (
    <div ref={setDroppableRef} className="relative" data-testid="svg-canvas-wrap">
      <svg
        ref={(el) => {
          svgRef.current = el;
        }}
        data-testid="builder-canvas-svg"
        width={canvasWidth}
        height={canvasHeight}
        viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
        className="block max-w-full touch-none rounded-md border border-border bg-card"
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => setPointer(null)}
        onClick={(e) => {
          // 点击空白处：命中测试后选中（或取消）
          const p = toSvgPoint(e);
          const id = geometryHitTest(p.x, p.y, rects);
          onSelect(id);
        }}
      >
        {/* 根节点背景（页面容器） */}
        <rect
          data-testid="svg-root"
          x={rootRect?.x ?? 0}
          y={rootRect?.y ?? 0}
          width={canvasWidth}
          height={canvasHeight}
          className={cn("fill-muted/20 stroke-muted-foreground/20", isRootSelected && "stroke-primary")}
          strokeWidth={1}
          rx={4}
        />

        {rects
          .filter((r) => r.depth > 0)
          .map((r) => {
            const isSelected = r.id === selectedNodeId;
            const isHovered = r.id === hoveredNodeId;
            const isDragging = drag?.id === r.id;
            const viewRect = isDragging && preview ? preview.rect : r;
            const rectNode = nodeMap.get(r.id);
            const rectLabel = rectNode
              ? (getMaterial(rectNode.type)?.label ?? rectNode.type)
              : r.id;
            return (
              <g
                key={r.id}
                data-testid="svg-node"
                data-node-id={r.id}
                data-depth={r.depth}
                data-x={viewRect.x}
                data-y={viewRect.y}
                data-width={viewRect.width}
                data-height={viewRect.height}
                transform={`translate(${viewRect.x}, ${viewRect.y})`}
                className="group cursor-pointer outline-none"
                role="button"
                tabIndex={0}
                aria-label={`选择组件 ${rectLabel}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(r.id);
                }}
                onKeyDown={(e) => {
                  // 键盘等价：Enter / Space 选中（与 onClick 对齐）
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelect(r.id);
                  }
                }}
                onPointerDown={(e) => startDrag(r, e)}
                onPointerEnter={() => onHover(r.id)}
                onPointerLeave={() => onHover(null)}
              >
                <rect
                  width={viewRect.width}
                  height={viewRect.height}
                  className={cn(
                    "fill-white/60 stroke-muted-foreground/40",
                    "group-focus-visible:stroke-primary group-focus-visible:stroke-2",
                    isDragging && "fill-primary/20 stroke-primary",
                    isSelected && "stroke-primary",
                    isHovered && !isSelected && "stroke-muted-foreground/80",
                  )}
                  strokeWidth={1}
                  rx={4}
                />
                {renderNodeBody(rectNode, viewRect.width, viewRect.height)}
              </g>
            );
          })}

        {/* 选中框（SVG rect 勾勒） */}
        {selectedNodeId && (
          (() => {
            const r = rects.find((x) => x.id === selectedNodeId);
            if (!r) return null;
            const view = drag?.id === r.id && preview ? preview.rect : r;
            return (
              <rect
                data-testid="svg-selection"
                data-node-id={r.id}
                x={view.x}
                y={view.y}
                width={view.width}
                height={view.height}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth={1.5}
                strokeDasharray="4 2"
              />
            );
          })()
        )}

        {/* 对齐参考线（吸附时以 SVG line 绘制） */}
        {guides.map((g, i) =>
          g.axis === "x" ? (
            <line
              key={`gx-${i}`}
              data-testid="svg-guide"
              data-axis="x"
              data-position={g.position}
              x1={g.position}
              x2={g.position}
              y1={0}
              y2={canvasHeight}
              stroke="hsl(var(--warning))"
              strokeWidth={1}
              strokeDasharray="5 3"
            />
          ) : (
            <line
              key={`gy-${i}`}
              data-testid="svg-guide"
              data-axis="y"
              data-position={g.position}
              x1={0}
              x2={canvasWidth}
              y1={g.position}
              y2={g.position}
              stroke="hsl(var(--warning))"
              strokeWidth={1}
              strokeDasharray="5 3"
            />
          ),
        )}
      </svg>
    </div>
  );
}

/** 节点内容映射：rect/text/image 基本映射（其余类型显示类型名）。 */
function renderNodeBody(
  n: ComponentNode | undefined,
  width: number,
  height: number,
): React.ReactNode {
  if (!n) return null;
  const label = nodeLabel(n);
  if (n.type === "Image") {
    const src = String(n.props?.src ?? "");
    return src ? (
      <image href={src} x={0} y={0} width={width} height={height} preserveAspectRatio="xMidYMid meet" />
    ) : (
      <text x={width / 2} y={height / 2} textAnchor="middle" dominantBaseline="central" className="fill-muted-foreground text-[10px]">
        🖼 图片
      </text>
    );
  }
  if (label) {
    const fontSize = n.type === "Text" ? Math.max(8, Math.min(16, Number(n.props?.fontSize ?? 14))) : 11;
    return (
      <text
        x={n.type === "Button" ? width / 2 : 6}
        y={n.type === "Button" ? height / 2 : height / 2}
        textAnchor={n.type === "Button" ? "middle" : "start"}
        dominantBaseline="central"
        fontSize={fontSize}
        className="fill-foreground/80"
      >
        {truncate(label, Math.max(1, Math.floor(width / 7)))}
      </text>
    );
  }
  return null;
}

export class SvgRenderer implements CanvasRenderer {
  readonly mode = "svg" as const;

  render(ctx: CanvasRendererContext): React.ReactNode {
    return (
      <SvgCanvas
        node={ctx.node}
        selectedNodeId={ctx.selectedNodeId}
        hoveredNodeId={ctx.hoveredNodeId}
        onSelect={ctx.onSelect}
        onHover={ctx.onHover}
        onMoveChild={ctx.onMoveChild}
      />
    );
  }

  layout(ctx: CanvasRendererContext): LayoutRect[] {
    return computeLayout(ctx.node);
  }

  hitTest(ctx: CanvasRendererContext, x: number, y: number): string | null {
    return geometryHitTest(x, y, this.layout(ctx));
  }
}
